/**
 * Snapping utilities for field placement
 */

const GRID_SIZE_PX = 8; // Grid size in pixels
const ALIGNMENT_THRESHOLD_PX = 6; // Snap to alignment within 6px

/**
 * Calculate grid snap for normalized coordinates
 */
export function snapToGrid(normValue, pageSize, scale) {
  const pxValue = normValue * pageSize * scale;
  const gridNorm = GRID_SIZE_PX / (pageSize * scale);
  const snappedNorm = Math.round(normValue / gridNorm) * gridNorm;
  return snappedNorm;
}

/**
 * Find alignment snap targets
 * Returns { x?, y?, guides: [] }
 */
export function findAlignmentSnap(field, fields, pageMetrics, scale) {
  const snapResult = {
    x: null,
    y: null,
    guides: [],
  };
  
  // Get nearest 3 fields on same page
  const neighbors = fields
    .filter((f) => f.page === field.page && f.id !== field.id)
    .map((f) => ({
      ...f,
      distX: Math.abs(f.x - field.x),
      distY: Math.abs(f.y - field.y),
    }))
    .sort((a, b) => (a.distX + a.distY) - (b.distX + b.distY))
    .slice(0, 3);
  
  const thresholdNorm = ALIGNMENT_THRESHOLD_PX / (pageMetrics.width * scale);
  
  // Check horizontal alignment (x, x+w, x+w/2)
  for (const n of neighbors) {
    // Left edges
    if (Math.abs(field.x - n.x) < thresholdNorm) {
      snapResult.x = n.x;
      snapResult.guides.push({
        type: 'vertical',
        x: n.x,
        label: 'left',
      });
      break;
    }
    // Right edges
    if (Math.abs((field.x + field.w) - (n.x + n.w)) < thresholdNorm) {
      snapResult.x = n.x + n.w - field.w;
      snapResult.guides.push({
        type: 'vertical',
        x: n.x + n.w,
        label: 'right',
      });
      break;
    }
    // Centers
    const fieldCenterX = field.x + field.w / 2;
    const nCenterX = n.x + n.w / 2;
    if (Math.abs(fieldCenterX - nCenterX) < thresholdNorm) {
      snapResult.x = nCenterX - field.w / 2;
      snapResult.guides.push({
        type: 'vertical',
        x: nCenterX,
        label: 'center',
      });
      break;
    }
  }
  
  // Check vertical alignment (y, y+h, y+h/2)
  for (const n of neighbors) {
    // Top edges
    if (Math.abs(field.y - n.y) < thresholdNorm) {
      snapResult.y = n.y;
      snapResult.guides.push({
        type: 'horizontal',
        y: n.y,
        label: 'top',
      });
      break;
    }
    // Bottom edges
    if (Math.abs((field.y + field.h) - (n.y + n.h)) < thresholdNorm) {
      snapResult.y = n.y + n.h - field.h;
      snapResult.guides.push({
        type: 'horizontal',
        y: n.y + n.h,
        label: 'bottom',
      });
      break;
    }
    // Centers
    const fieldCenterY = field.y + field.h / 2;
    const nCenterY = n.y + n.h / 2;
    if (Math.abs(fieldCenterY - nCenterY) < thresholdNorm) {
      snapResult.y = nCenterY - field.h / 2;
      snapResult.guides.push({
        type: 'horizontal',
        y: nCenterY,
        label: 'center',
      });
      break;
    }
  }
  
  return snapResult;
}

/**
 * Apply snapping to field rect (alignment preferred over grid)
 */
export function applySnapping(field, fields, pageMetrics, scale) {
  const alignmentSnap = findAlignmentSnap(field, fields, pageMetrics, scale);
  
  let snappedX = field.x;
  let snappedY = field.y;
  
  // Prefer alignment snap over grid
  if (alignmentSnap.x !== null) {
    snappedX = alignmentSnap.x;
  } else {
    snappedX = snapToGrid(field.x, pageMetrics.width, scale);
  }
  
  if (alignmentSnap.y !== null) {
    snappedY = alignmentSnap.y;
  } else {
    snappedY = snapToGrid(field.y, pageMetrics.height, scale);
  }
  
  return {
    x: snappedX,
    y: snappedY,
    w: field.w,
    h: field.h,
    guides: alignmentSnap.guides,
  };
}
